import React, { useCallback } from "react";
import { useField } from "formik";
import { Checkbox, Flex } from "@jsq/ds/core";
import { FormTextInput } from "@jsq/ds/forms";
import { FieldGroup } from "../../common/components/Form/Layout/FieldGroup";
import { MultiInputFormRow } from "../../common/components/Form/Layout/MultiInputFormRow";
import { SimpleFormRow } from "../../common/components/Form/Layout/SimpleFormRow";
import { StateSelectDropdown } from "../../common/components/StateSelectDropdown";
import { isUSA } from "../../common/utils";
import { addressFormRow } from "./styles_vanilla.css";
import { type SharedAddressInputProps } from "./types";

/**
 * AddressInput component represents an input form for capturing address information.
 *
 * @component
 * @example
 * ```tsx
 * <AddressInput
 *   title="Billing Address"
 *   description="Enter your billing address"
 *   onCountryChange={handleCountryChange}
 * />
 * ```
 *
 * @param {SharedAddressInputProps} props - The props for the SharedAddressInput component.
 * @param {string} props.title - The title of the address input form.
 * @param {string} props.description - The description of the address input form.
 * @param {React.FunctionComponent} props.CountrySelect - The country select component.
 * @param {Function} props.onCountryChange - The callback function to handle country change.
 * @param {boolean} props.includeSameAsMailingAddressField - Flag to enable the same as mailing address field.
 * @param {string} props.sameAsMailingAddressLabel - The label for the "same as mailing address" checkbox.
 * @param {string} [props.name=""] - The base name for the form fields.
 * @returns {React.FC<AddressInputProps>} The AddressInput component.
 *
 * @todo Ideally a significant amount of validation would occur on this form to ensure good data.
 */
export const SharedAddressInput: React.FC<SharedAddressInputProps> = ({
  title,
  description,
  CountrySelect,
  onCountryChange = undefined,
  includeSameAsMailingAddressField = false,
  sameAsMailingAddressLabel = "Same as mailing address",
  name = "",
  testIsUSA = isUSA,
  fieldNames,
  meta,
}: SharedAddressInputProps) => {
  const fieldName = (field: string) => (name ? `${name}.${field}` : field);

  const sameAsMailingAddressFieldName = fieldName("sameAsMailingAddress");
  const stateFieldName = fieldNames?.state || fieldName("state");
  const countryFieldName = fieldNames?.country || fieldName("country");
  const provinceFieldName = fieldNames?.province || fieldName("province");
  const streetLine1FieldName = fieldNames?.streetLine1 || fieldName("streetLine1");
  const streetLine2FieldName = fieldNames?.streetLine2 || fieldName("streetLine2");
  const cityFieldName = fieldNames?.city || fieldName("city");
  const zipFieldName = fieldNames?.zip || fieldName("zip");

  const [sameAsMailingAddress, , sameAsMailingAddressHelpers] = useField<boolean>(
    sameAsMailingAddressFieldName,
  );
  const [, , stateHelpers] = useField<string | undefined>(stateFieldName);
  const [countryField, , countryHelpers] = useField<string | undefined>(countryFieldName);
  const [, , provinceHelpers] = useField<string | undefined>(provinceFieldName);
  const [, , streetLine1Helpers] = useField<string | undefined>(streetLine1FieldName);
  const [, , streetLine2Helpers] = useField<string | undefined>(streetLine2FieldName);
  const [, , cityHelpers] = useField<string | undefined>(cityFieldName);
  const [, , zipHelpers] = useField<string | undefined>(zipFieldName);

  const handleCountryChange = (value: string | number | string[] | number[]) => {
    if (typeof value === "string" && value !== countryField.value) {
      if (testIsUSA(value)) {
        void provinceHelpers.setValue("");
      } else {
        void stateHelpers.setValue("");
      }
    }
    onCountryChange?.();
  };

  const handleCheckboxChange = useCallback(() => {
    if (includeSameAsMailingAddressField) {
      const clearAddressFields = () => {
        void countryHelpers.setValue("");
        void stateHelpers.setValue("");
        void provinceHelpers.setValue("");
        void streetLine1Helpers.setValue("");
        void streetLine2Helpers.setValue("");
        void cityHelpers.setValue("");
        void zipHelpers.setValue("");
      };

      const newValue = !sameAsMailingAddress.value;
      void sameAsMailingAddressHelpers.setValue(newValue);
      if (newValue) {
        clearAddressFields();
      }
    }
  }, [
    includeSameAsMailingAddressField,
    sameAsMailingAddress.value,
    sameAsMailingAddressHelpers,
    countryHelpers,
    stateHelpers,
    provinceHelpers,
    streetLine1Helpers,
    streetLine2Helpers,
    cityHelpers,
    zipHelpers,
  ]);

  const isCountryUSA = testIsUSA(countryField.value);

  return (
    <FieldGroup title={title} description={description} meta={meta}>
      {includeSameAsMailingAddressField && (
        <SimpleFormRow className={addressFormRow}>
          <Checkbox
            checked={sameAsMailingAddress.value}
            onChange={handleCheckboxChange}
            name={sameAsMailingAddressFieldName}
            label={sameAsMailingAddressLabel}
          />
        </SimpleFormRow>
      )}
      {!sameAsMailingAddress.value && (
        <>
          {CountrySelect ? (
            <Flex className={addressFormRow}>
              <CountrySelect
                key="country"
                name={countryFieldName}
                placeholder="Select a country"
                handleChange={handleCountryChange}
              />
            </Flex>
          ) : null}
          <SimpleFormRow className={addressFormRow}>
            <FormTextInput placeholder="Street" isFullWidth name={streetLine1FieldName} />
          </SimpleFormRow>
          <SimpleFormRow className={addressFormRow}>
            <FormTextInput
              placeholder="Apartment, suite, etc. (optional)"
              isFullWidth
              name={streetLine2FieldName}
            />
          </SimpleFormRow>
          <SimpleFormRow className={addressFormRow}>
            <FormTextInput placeholder="City" isFullWidth name={cityFieldName} />
          </SimpleFormRow>
          <MultiInputFormRow>
            {isCountryUSA ? (
              <StateSelectDropdown key="state" placeholder="State" name={stateFieldName} />
            ) : (
              <FormTextInput
                key="province"
                isFullWidth
                name={provinceFieldName}
                placeholder="State/Province"
              />
            )}
            <FormTextInput
              key="zip"
              placeholder="ZIP/Postal code"
              isFullWidth
              name={zipFieldName}
            />
          </MultiInputFormRow>
        </>
      )}
    </FieldGroup>
  );
};
